import { useLayoutEffect, useRef, type RefObject } from 'react';
import {
  LIST_VIEW_RESTORE,
  readListViewSnapshot,
  writeListViewScroll,
  type ListViewScrollPos,
} from '../utils/listViewSession';

const SCROLL_PERSIST_MS = 80;
const RESTORE_MAX_FRAMES = 90;
const RESTORE_MAX_BURSTS = 24;

function isScrollableOverflow(value: string): boolean {
  return value === 'auto' || value === 'scroll';
}

function findTableScrollElement(root: HTMLElement): HTMLElement | null {
  const nodes = Array.from(
    root.querySelectorAll<HTMLElement>(
      '.ant-table-body, .ant-table-content, .ant-table-tbody-virtual-holder',
    ),
  );
  const withOverflow = nodes.filter(el => {
    const style = window.getComputedStyle(el);
    return isScrollableOverflow(style.overflowY) || isScrollableOverflow(style.overflowX);
  });
  const overflowing = withOverflow.find(
    el => el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1,
  );
  const withOffset = [...withOverflow, ...nodes].find(el => el.scrollTop > 0 || el.scrollLeft > 0);
  return withOffset ?? overflowing ?? withOverflow[0] ?? nodes[0] ?? null;
}

function syncHeaderScroll(root: HTMLElement, left: number): void {
  const header = root.querySelector('.ant-table-header');
  if (header instanceof HTMLElement) header.scrollLeft = left;
}

function applyScroll(root: HTMLElement, el: HTMLElement, top: number, left: number): void {
  el.scrollTop = top;
  el.scrollLeft = left;
  syncHeaderScroll(root, left);
}

function readPos(el: HTMLElement): ListViewScrollPos {
  return { top: el.scrollTop, left: el.scrollLeft };
}

function hasScrollOffset(pos: ListViewScrollPos | null | undefined): pos is ListViewScrollPos {
  return pos != null && (pos.top > 0 || pos.left > 0);
}

/**
 * 把 IpcTable 表体滚动写入同一份列表会话快照，并在回到列表后恢复。
 * 仅在传入 listViewKey 且 LIST_VIEW_RESTORE.scroll 打开时生效；弹框表格不要传 key。
 */
export function useListViewTableScroll(
  listViewKey: string | undefined,
  shellRef: RefObject<HTMLElement | null>,
  options: {
    ready: boolean;
    page: number;
    pageSize: number;
    layoutKey: unknown;
  },
): void {
  const enabled = Boolean(listViewKey) && LIST_VIEW_RESTORE.scroll;
  const userScrolledRef = useRef(false);
  const restoringRef = useRef(false);
  const leaveLockRef = useRef(false);
  const lastPosRef = useRef<ListViewScrollPos | null>(null);
  const persistTimerRef = useRef(0);
  const pageRef = useRef({ page: options.page, pageSize: options.pageSize });

  useLayoutEffect(() => {
    userScrolledRef.current = false;
    leaveLockRef.current = false;
    lastPosRef.current = listViewKey
      ? readListViewSnapshot(listViewKey)?.scroll ?? null
      : null;
  }, [listViewKey]);

  useLayoutEffect(() => {
    if (!enabled || !listViewKey) return undefined;
    const shell = shellRef.current;
    if (!shell) return undefined;

    const persistNow = () => {
      const next = lastPosRef.current;
      if (next == null) return;
      writeListViewScroll(listViewKey, hasScrollOffset(next) ? next : null);
    };

    const persistSoon = () => {
      window.clearTimeout(persistTimerRef.current);
      persistTimerRef.current = window.setTimeout(persistNow, SCROLL_PERSIST_MS);
    };

    const captureFromEl = (el: HTMLElement | null) => {
      if (!el) return;
      const pos = readPos(el);
      if (!hasScrollOffset(pos) && hasScrollOffset(lastPosRef.current)) return;
      lastPosRef.current = pos;
    };

    const onWheelOrTouch = () => {
      if (restoringRef.current) return;
      leaveLockRef.current = false;
      userScrolledRef.current = true;
      captureFromEl(findTableScrollElement(shell));
      persistSoon();
    };

    const onPointerDown = (event: PointerEvent) => {
      if (restoringRef.current) return;
      captureFromEl(findTableScrollElement(shell));
      persistNow();
      const target = event.target;
      if (target instanceof Element && target.closest('a, button, tr, .ipc-link, [role="button"]')) {
        leaveLockRef.current = true;
        return;
      }
      userScrolledRef.current = true;
    };

    const onScroll = (event: Event) => {
      if (restoringRef.current || leaveLockRef.current) return;
      if (!userScrolledRef.current) return;
      const target = event.target;
      captureFromEl(target instanceof HTMLElement && shell.contains(target) ? target : null);
      persistSoon();
    };

    shell.addEventListener('scroll', onScroll, { passive: true, capture: true });
    shell.addEventListener('wheel', onWheelOrTouch, { passive: true, capture: true });
    shell.addEventListener('touchmove', onWheelOrTouch, { passive: true, capture: true });
    shell.addEventListener('pointerdown', onPointerDown, { capture: true });
    window.addEventListener('pagehide', persistNow);

    return () => {
      window.clearTimeout(persistTimerRef.current);
      persistNow();
      shell.removeEventListener('scroll', onScroll, true);
      shell.removeEventListener('wheel', onWheelOrTouch, true);
      shell.removeEventListener('touchmove', onWheelOrTouch, true);
      shell.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('pagehide', persistNow);
    };
  }, [enabled, listViewKey, shellRef]);

  useLayoutEffect(() => {
    const prev = pageRef.current;
    pageRef.current = { page: options.page, pageSize: options.pageSize };
    if (!enabled || !listViewKey) return;
    if (prev.page === options.page && prev.pageSize === options.pageSize) return;
    const shell = shellRef.current;
    if (!shell) return;
    const el = findTableScrollElement(shell);
    if (el) applyScroll(shell, el, 0, 0);
    lastPosRef.current = { top: 0, left: 0 };
    userScrolledRef.current = false;
  }, [enabled, listViewKey, options.page, options.pageSize, shellRef]);

  useLayoutEffect(() => {
    if (!enabled || !listViewKey || !options.ready) return undefined;
    const shell = shellRef.current;
    if (!shell) return undefined;

    let cancelled = false;
    let frames = 0;
    let bursts = 0;
    let raf = 0;
    const target = readListViewSnapshot(listViewKey)?.scroll ?? lastPosRef.current;
    if (!hasScrollOffset(target)) return undefined;

    const run = () => {
      if (cancelled || userScrolledRef.current) return;
      const el = findTableScrollElement(shell);
      if (!el) {
        if (frames < RESTORE_MAX_FRAMES) {
          frames += 1;
          raf = window.requestAnimationFrame(run);
        }
        return;
      }
      restoringRef.current = true;
      applyScroll(shell, el, target.top, target.left);
      restoringRef.current = false;
      if (frames >= RESTORE_MAX_FRAMES) return;
      frames += 1;
      raf = window.requestAnimationFrame(run);
    };

    const onProgrammaticScroll = () => {
      if (cancelled || userScrolledRef.current || restoringRef.current) return;
      if (bursts >= RESTORE_MAX_BURSTS) return;
      bursts += 1;
      frames = 0;
      window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(run);
    };

    shell.addEventListener('scroll', onProgrammaticScroll, true);
    run();
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
      shell.removeEventListener('scroll', onProgrammaticScroll, true);
    };
  }, [enabled, listViewKey, options.ready, options.layoutKey, shellRef]);
}
